import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Check, Command, LayoutGrid, Circle, Activity, Globe, Aperture } from 'lucide-react';
export type AvatarStyleType = 'gradient1' | 'gradient2' | 'gradient3' | 'gradient4' | 'gradient5';
interface StyleSwitcherProps {
  currentStyle: AvatarStyleType;
  onStyleChange: (style: AvatarStyleType) => void;
}
const STYLES: {
  id: AvatarStyleType;
  name: string;
  desc: string;
  icon: React.ElementType;
  shortcut: string;
}[] = [{
  id: 'gradient1',
  name: 'Bright Gradient',
  desc: 'Balanced gold & blue',
  icon: Aperture,
  shortcut: '1'
}, {
  id: 'gradient2',
  name: 'Bold Bloom',
  desc: 'Large immersive orbs',
  icon: Globe,
  shortcut: '2'
}, {
  id: 'gradient3',
  name: 'Golden Lead',
  desc: 'Gold forward, blue activates',
  icon: Sparkles,
  shortcut: '3'
}, {
  id: 'gradient4',
  name: 'Navy Lead',
  desc: 'Blue forward, gold activates',
  icon: Activity,
  shortcut: '4'
}, {
  id: 'gradient5',
  name: 'Compact Duo',
  desc: 'Tight & contained',
  icon: Circle,
  shortcut: '5'
}];
export function StyleSwitcher({
  currentStyle,
  onStyleChange
}: StyleSwitcherProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const activeStyle = STYLES.find(s => s.id === currentStyle) || STYLES[0];
  return <div className="fixed bottom-6 right-6 z-50" ref={containerRef} data-id="element-17">
      <AnimatePresence data-id="element-18">
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: 20,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.95
      }} transition={{
        duration: 0.2
      }} className="absolute bottom-16 right-0 w-80 bg-slate-900/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl overflow-hidden" data-id="element-19">
            <div className="p-4 border-b border-white/5" data-id="element-20">
              <div className="flex items-center justify-between" data-id="element-21">
                <h3 className="text-white font-medium flex items-center gap-2" data-id="element-22">
                  <Aperture className="w-4 h-4 text-blue-400" data-id="element-23" />
                  Avatar Style
                </h3>
                <span className="text-xs text-slate-500" data-id="element-24">Press 1-5</span>
              </div>
            </div>

            <div className="p-2 space-y-1" data-id="element-25">
              {STYLES.map(style => <button key={style.id} onClick={() => {
            onStyleChange(style.id);
            setIsOpen(false);
          }} className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all text-left group ${currentStyle === style.id ? 'bg-blue-600/20 hover:bg-blue-600/30' : 'hover:bg-white/5'}`} data-id="element-26">
                  <div className={`p-2 rounded-lg ${currentStyle === style.id ? 'bg-blue-500 text-white' : 'bg-slate-800 text-slate-400 group-hover:text-slate-200'}`} data-id="element-27">
                    <style.icon className="w-4 h-4" data-id="element-28" />
                  </div>

                  <div className="flex-1" data-id="element-29">
                    <div className={`text-sm font-medium ${currentStyle === style.id ? 'text-blue-200' : 'text-slate-200'}`} data-id="element-30">
                      {style.name}
                    </div>
                    <div className="text-xs text-slate-500 group-hover:text-slate-400" data-id="element-31">
                      {style.desc}
                    </div>
                  </div>

                  {currentStyle === style.id ? <Check className="w-4 h-4 text-blue-400" data-id="element-32" /> : <div className="w-5 h-5 rounded border border-white/10 flex items-center justify-center text-[10px] text-slate-500 font-mono" data-id="element-33">
                      {style.shortcut}
                    </div>}
                </button>)}
            </div>
          </motion.div>}
      </AnimatePresence>

      <motion.button whileHover={{
      scale: 1.05
    }} whileTap={{
      scale: 0.95
    }} onClick={() => setIsOpen(!isOpen)} className={`flex items-center gap-2 px-4 py-3 rounded-full shadow-lg border backdrop-blur-md transition-colors ${isOpen ? 'bg-blue-600 border-blue-500 text-white' : 'bg-slate-900/80 border-white/10 text-slate-300 hover:bg-slate-800 hover:text-white'}`} data-id="element-34">
        <activeStyle.icon className="w-4 h-4" data-id="element-35" />
        <span className="text-sm font-medium" data-id="element-36">{activeStyle.name}</span>
        <span className="ml-1 px-1.5 py-0.5 rounded bg-white/10 text-[10px] font-mono" data-id="element-37">
          {activeStyle.shortcut}
        </span>
      </motion.button>
    </div>;
}